import { useState } from 'react';
import { SEO } from '../../utils/seo';
import HeroSection from './components/HeroSection';
import AddressInput from './components/AddressInput';
import MapViewer from './components/MapViewer';
import QuoteForm from './components/QuoteForm';
import QuoteResults from './components/QuoteResults';
import type { FieldData, QuoteCalculation } from './types';

export default function InstantQuotePage() {
  const [fieldData, setFieldData] = useState<FieldData | null>(null);
  const [quoteResults, setQuoteResults] = useState<QuoteCalculation | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Instant Quote Calculator for Precision Aerial Application',
    description:
      'Get an instant, accurate quote for drone spraying services based on your field location, acreage, and complexity',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
  };

  const handleFieldConfirmed = (data: FieldData) => {
    setFieldData(data);
  };

  const handleQuoteCalculated = (calculation: QuoteCalculation) => {
    setQuoteResults(calculation);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Instant Quote Calculator - Get Your Price Now | Greenfield Partners"
        description="Get an instant, accurate quote for precision aerial application services. Enter your field location, confirm acreage, and see transparent pricing based on field complexity and service options."
        keywords="drone spraying quote, aerial application pricing, instant farm quote, precision agriculture cost, field spraying estimate"
        schema={schema}
      />
      <HeroSection />

      {/* Main Calculator Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Step 1: Address Input */}
          {!fieldData && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-4">
                  <span className="text-emerald-800 font-bold text-lg">1</span>
                </div>
                <h2 className="text-3xl font-bold mb-3">Locate Your Field</h2>
                <p className="text-gray-600">
                  Enter your property address or GPS coordinates to get started
                </p>
              </div>
              <AddressInput onFieldConfirmed={handleFieldConfirmed} />
            </div>
          )}

          {/* Step 2 & 3: Map + Quote Form */}
          {fieldData && !quoteResults && (
            <div className="grid lg:grid-cols-[1fr_420px] gap-8">
              {/* Left: Map */}
              <div>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-4">
                    <span className="text-emerald-800 font-bold text-lg">2</span>
                  </div>
                  <h2 className="text-3xl font-bold mb-3">
                    Confirm Your Field Boundaries
                  </h2>
                  <p className="text-gray-600">
                    Review the field outline and adjust if needed
                  </p>
                </div>
                <MapViewer fieldData={fieldData} onFieldUpdated={setFieldData} />
              </div>

              {/* Right: Quote Form */}
              <div>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-4">
                    <span className="text-emerald-800 font-bold text-lg">3</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-3">Service Details</h2>
                  <p className="text-sm text-gray-600">
                    Tell us about your application needs
                  </p>
                </div>
                <QuoteForm fieldData={fieldData} onQuoteCalculated={handleQuoteCalculated} />
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {quoteResults && fieldData && (
            <div>
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-4">
                  <i className="ri-check-line text-emerald-800 text-xl"></i>
                </div>
                <h2 className="text-3xl font-bold mb-3">Your Instant Quote</h2>
                <p className="text-gray-600">
                  Based on your field characteristics and service requirements
                </p>
              </div>
              <QuoteResults
                calculation={quoteResults}
                fieldData={fieldData}
                onStartOver={() => {
                  setFieldData(null);
                  setQuoteResults(null);
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 px-6 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-emerald-50 rounded-full">
                <i className="ri-shield-check-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Licensed &amp; Insured</h3>
              <p className="text-sm text-gray-600">
                FAA Part 107 certified pilots with full liability coverage
              </p>
            </div>
            <div>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-emerald-50 rounded-full">
                <i className="ri-map-pin-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">GPS Precision</h3>
              <p className="text-sm text-gray-600">
                Centimeter-level accuracy for uniform coverage
              </p>
            </div>
            <div>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-emerald-50 rounded-full">
                <i className="ri-time-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Fast Response</h3>
              <p className="text-sm text-gray-600">
                Same-day or next-day service during peak season
              </p>
            </div>
            <div>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-emerald-50 rounded-full">
                <i className="ri-leaf-line text-2xl text-emerald-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Zero Compaction</h3>
              <p className="text-sm text-gray-600">
                Protect your soil structure and maximize yield
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="h-40 bg-gradient-to-b from-white via-stone-200/40 to-[#1a1f1c]"></div>
      <section className="py-20 px-6 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-wide">
            Questions About Your Quote?
          </h2>
          <p className="text-lg text-gray-300 mb-8 font-light leading-relaxed">
            Our team is here to walk you through the details and answer any
            questions about your specific field conditions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+17179131780"
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call 717-913-1780
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-bold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Send a Message
            </a>
          </div>
        </div>
      </section>

      <div className="h-px bg-white/8"></div>
    </div>
  );
}
